import { Injectable, computed, inject, signal } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { DialogGeojsonEditorComponent } from '@components/dialogs/dialog-geojson-editor/dialog-geojson-editor.component'
import { HttpService } from '@core/bases/http.service'
import { API_URL } from '@core/constants/constant.data'
import { LAYERS_ARRAY } from '@core/constants/layers.data'
import { ArrayIndex, GeojsonPathElement } from '@core/models/geojson-edition.model'
import { backendResponse } from '@core/models/http.model'
import { LayerUploaded } from '@core/models/layers.model'
import { SLDService } from '@core/services/sld.service'
import { UploadedLayersService } from './layers-uploaded.service'

@Injectable({ providedIn: 'root' })
export class GeojsonEditorService extends HttpService {
  private readonly _dialog = inject(MatDialog)
  private readonly _sldService = inject(SLDService)
  private readonly _uploadedLayersService = inject(UploadedLayersService)

  geojson$ = signal<any>(null)
  sld$ = signal<any | null>(null)
  lastClosedPopup$ = signal<number | null>(null)

  originalGeojson: any = null
  protected override _baseUrl = API_URL + '/upload'
  featureLayerMap = new Map<number, L.Layer>()

  // Computed property to list all changed properties between geojson$ and originalGeojson
  changesList$ = computed(() => {
    const current = this.geojson$()
    const original = this.originalGeojson
    if (!current || !original) return []

    const changes: {
      featureIndex: number
      path: (string | number)[]
      originalValue: any
      currentValue: any
    }[] = []

    if (Array.isArray(current.features) && Array.isArray(original.features)) {
      for (let i = 0; i < current.features.length; i++) {
        const curProps = current.features[i]?.properties || {}
        const origProps = original.features[i]?.properties || {}
        this._collectChanges(curProps, origProps, [], i, changes)
      }
    }

    return changes
  })

  clear() {
    this.geojson$.set(null)
    this.sld$.set(null)
    this.lastClosedPopup$.set(null)
  }

  async openEditorDialog(layer: LayerUploaded): Promise<void> {
    this.clear()
    await this._loadLayerAndSld(layer)

    this._dialog
      .open(DialogGeojsonEditorComponent, {
        width: '90vw',
        height: '90vh',
        data: {
          layer: layer,
        },
        maxWidth: '100vw', // Mandatory to width applied
        disableClose: true,
      })
      .afterClosed()
      .subscribe(async (result) => {
        if (!result || result.saved === false) return
        await this._uploadedLayersService.actionLayer('personal', layer)
        await this._uploadedLayersService.list()
        const targetLayerId = result.savedAsNew ? result.id : layer.id
        const targetLayer = this._uploadedLayersService
          .personalLayers$()
          .find((l) => l.id === targetLayerId)
        if (targetLayer) {
          await this._uploadedLayersService.actionLayer('personal', targetLayer)
        }
      })
  }

  updateGeoJSON(uploadId: number, geojson: any): Promise<backendResponse> {
    const url = `${this._baseUrl}/geojson/${uploadId}`
    return this.PUT(uploadId, geojson, url)
  }

  private async _loadLayerAndSld(layer: LayerUploaded) {
    if (!layer?.name?.endsWith('.geojson')) return

    const url = this._baseUrl + '/geojson/' + layer.id
    const geojson = await this.GETobject(layer.id, url)
    if (geojson === null) return

    // Deep copy for originalGeojson
    this.originalGeojson = JSON.parse(JSON.stringify(geojson))

    this.geojson$.set(geojson)
    const layersWithSameType = LAYERS_ARRAY.filter(
      (l) => l.layer_type?.includes(layer.layer_type) && l.id == 0,
    )

    // if this type of layer exit, we load sld style
    let xmlSld
    if (layersWithSameType.length > 0) {
      xmlSld = await this._sldService.getStyleFromGeoserver(layer.layer_type)
    }

    this.sld$.set(xmlSld ?? null)
  }

  isArrayIndex(segment: unknown): segment is ArrayIndex {
    return (
      typeof segment === 'object' &&
      segment !== null &&
      'index' in segment &&
      typeof (segment as any).index === 'number'
    )
  }

  private _collectChanges(
    cur: any,
    orig: any,
    path: GeojsonPathElement[] = [],
    featureIndex: number,
    changes: {
      featureIndex: number
      path: GeojsonPathElement[]
      originalValue: any
      currentValue: any
    }[],
  ) {
    // If both are arrays
    if (Array.isArray(cur) && Array.isArray(orig)) {
      const maxLen = Math.max(cur.length, orig.length)
      for (let i = 0; i < maxLen; i++) {
        this._collectChanges(cur[i], orig[i], [...path, { index: i }], featureIndex, changes)
      }
      return
    }

    // If both are objects (but not null)
    if (
      cur !== null &&
      orig !== null &&
      typeof cur === 'object' &&
      typeof orig === 'object' &&
      !Array.isArray(cur) &&
      !Array.isArray(orig)
    ) {
      const allKeys = new Set([...Object.keys(cur), ...Object.keys(orig)])
      for (const key of allKeys) {
        this._collectChanges(cur[key], orig[key], [...path, key], featureIndex, changes)
      }
      return
    }

    // Compare primitive values (including null/undefined)
    if (cur !== orig) {
      changes.push({
        featureIndex,
        path,
        originalValue: orig,
        currentValue: cur,
      })
    }
  }
}
